import React, { useCallback, useEffect } from 'react'
import { Col, Container, Row, Tab } from 'react-bootstrap'
import { Tabs } from '../../components/Tabs/Tabs'
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { PlanTab } from './PlanTab'
import { BillingTab } from './BillingTab'
import { WalletSection } from './WalletSection'
import { useGetBillingCustomerQuery, useGetPlansQuery } from '../../store/plan/plan'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useCustomWorkflowsEnabled } from '../../common/useCustomWorkflowsEnabled'

export const Plan: React.FC = () => {
  useDocumentTitle('Plan')
  const accountId = useAppSelector(selectAccountId)
  const { activeTab = 'plan' } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { hasQuota, isFetching: quotaIsFetching, quotaSuccess } = useZocksQuota()
  const { enabled: customWorkflowAvailable } = useCustomWorkflowsEnabled()

  const { data: customer } = useGetBillingCustomerQuery({ accountId })
  const { data: plans } = useGetPlansQuery(undefined)

  const customerPlan = plans?.data.find((p) => p.id === customer?.data.subscription?.planId)
  const crossClientAvailable = hasQuota('ai.crossClientQuery.value')
  const walletAvailable = crossClientAvailable || customWorkflowAvailable

  const hideBillingInfo = !customer?.data.subscription || !!customer.data.enterpriseSubscription?.enterpriseBillingConfig.package.hideBillingInfo

  useEffect(() => {
    if (!quotaSuccess && quotaIsFetching) {
      return
    }
    if (activeTab === 'wallet' && !walletAvailable) {
      navigate('/account/plan/plan', { replace: true })
    }
  }, [activeTab, quotaSuccess, quotaIsFetching, walletAvailable, navigate])

  const changeTab = useCallback(
    (key: string | null) => {
      if (key) {
        const period = searchParams.get('period')
        const checkoutSuccess = searchParams.get('checkoutSuccess')
        navigate(
          {
            pathname: `/account/plan/${key}`,
            search: createSearchParams({ ...(period && { period }), ...(checkoutSuccess && { checkoutSuccess }) }).toString(),
          },
          { replace: true },
        )
      }
    },
    [navigate, searchParams],
  )
  return (
    <Container>
      <Row className='mb-9'>
        <Col>
          <h3>Plan</h3>
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs onSelect={changeTab} activeKey={activeTab} id='plan-tabs' className='d-flex gap-2r mb-3'>
            <Tab className='mt-5' eventKey='plan' title='Plan' mountOnEnter={false}>
              <PlanTab />
            </Tab>
            {!hideBillingInfo ? (
              <Tab className='mt-5' eventKey='billing' title='Billing'>
                <BillingTab />
              </Tab>
            ) : null}
            {walletAvailable ? (
              <Tab className='mt-5' eventKey='wallet' title='Wallet'>
                <WalletSection
                  crossClientAvailable={crossClientAvailable}
                  customWorkflowAvailable={customWorkflowAvailable}
                  creditConfigs={customerPlan?.creditConfigs}
                />
              </Tab>
            ) : null}
          </Tabs>
        </Col>
      </Row>
    </Container>
  )
}
